import { useForm } from 'react-hook-form';

import CurrencyFormat from '../../../UI/Form/InputFormat/CurrencyFormat';
import { States } from '../../../../constants';
import Button from '../../../UI/Button';
import InputField from '../../../UI/Form/InputField';
import SelectField from '../../../UI/Form/SelectField';
import {
  acquisitionTypeOptions,
  constructionTypeOptions,
  dealTypeOptions,
  projectByRightOptions,
  siteControlStatusOptions,
  validationSchema,
} from './constants';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { developerApplicationSelector } from '../../../../store';
import { setProjectDetails } from '../../../../store/DeveloperApplication/ProjectDetails';
import MaskedInputField from '../../../UI/Form/MaskedInputField';
import NumericalFormat from '../../../UI/Form/InputFormat/NumericalFormat';
import { FileInputField } from '../../../UI/HookForm/Form/File/FileInputField';
import { useMutation } from '@tanstack/react-query';
import {
  PPSignedAssetResponse,
  signDeveloperAsset,
} from '../../../../api/Rover/partnerProposal';
import { UploadFnReturn, useFileUpload } from '../../../../hooks/useFileUpload';
import { useState } from 'react';
import { SignDeveloperAssetPayload } from '../../../../types/Shared';

export default function ProjectDetails({
  nextHandler,
}: {
  nextHandler: () => void;
}) {
  const dispatch = useDispatch();
  const [uploadError, setUploadError] = useState<string>('');
  const { projectDetails } = useSelector(developerApplicationSelector);
  const { upload } = useFileUpload({
    signAsset: signDeveloperAsset,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    trigger,
  } = useForm({
    defaultValues: projectDetails,
    resolver: yupResolver(validationSchema),
    mode: 'all',
  });

  const uploadFile = useMutation(
    (payload: {
      signAssetPayload: SignDeveloperAssetPayload[];
      files: File[];
      formState: any;
    }) => upload(payload),
    {
      onSuccess: ({ sign, files }: UploadFnReturn, { formState }) => {
        let signRes: PPSignedAssetResponse[] = [];
        if (Array.isArray(sign)) {
          signRes = [...sign];
        } else {
          signRes = [{ ...sign }];
        }

        updateState({
          ...formState,
          additionalDocs: signRes.map((sig, index) => ({
            name: files[index].name,
            path: sig.path,
            key: sig.key,
            type: files[index].type,
            field: 'additionalDocs',
            description: 'Additional Docs',
          })),
        });
      },
      onError: (error: any) => {
        setUploadError('Failed to upload Document');
      },
    }
  );

  const updateState = (data: any) => {
    dispatch(
      setProjectDetails({
        ...data,
        additionalDocs: [
          ...projectDetails.additionalDocs,
          ...data.additionalDocs,
        ],
        projectDetailsIsValid: true,
      })
    );
    nextHandler();
  };

  const onSubmit = (data: any) => {
    setUploadError('');
    if (data.selectedFiles?.length) {
      const signAssetPayload = data.selectedFiles.map((file: File) => ({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      }));
      uploadFile.mutate({
        signAssetPayload,
        files: data.selectedFiles,
        formState: data,
      });
    } else {
      updateState(data);
    }
  };

  const onError = (error: any) => {
    dispatch(setProjectDetails({ projectDetailsIsValid: false }));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
        <div>
          <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>
            Proposed Project Details
          </h3>
          <p className='mt-1 text-sm text-gray-500'>
            Details about your proposed project
          </p>
        </div>
        <div className='grid grid-cols-6 gap-6'>
          <InputField
            customClassName='col-span-6'
            label='Street Address'
            name='streetAddress'
            type='text'
            error={errors.streetAddress?.message}
            register={register}
          />
          <InputField
            customClassName='col-span-6 sm:col-span-6 lg:col-span-2'
            label='City'
            name='city'
            type='text'
            error={errors.city?.message}
            register={register}
          />
          <SelectField
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='State / Province'
            name='state'
            error={errors.state?.message}
            register={register}
          >
            <option>Select</option>
            {States.map((state) => (
              <option key={state.abbreviation} value={state.name}>
                {state.name}
              </option>
            ))}
          </SelectField>
          <InputField
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='Zip / Postal Code'
            name='zipCode'
            type='number'
            error={errors.zipCode?.message}
            register={register}
          />
          <SelectField
            customClassName='col-span-6 sm:col-span-2'
            label='Deal Type'
            name='dealType'
            error={errors.dealType?.message}
            register={register}
          >
            <option>Select</option>
            {dealTypeOptions.map((deal: string) => (
              <option key={deal} value={deal}>
                {deal}
              </option>
            ))}
          </SelectField>
          <MaskedInputField
            customClassName='col-span-6 sm:col-span-2'
            label='Proposed Number of Units'
            name='proposedUnits'
            id='proposedUnits'
            error={errors.proposedUnits?.message}
            control={control}
            MaskComponent={NumericalFormat}
          />
          <SelectField
            customClassName='col-span-6 lg:col-span-2'
            label='Is Proposed Project "By-Right"'
            name='projectByRight'
            error={errors.projectByRight?.message}
            register={register}
          >
            <option>Select</option>
            {projectByRightOptions.map((val: string) => (
              <option key={val} value={val}>
                {val}
              </option>
            ))}
          </SelectField>
          <SelectField
            customClassName='col-span-6 lg:col-span-2'
            label='Construction Type'
            name='constructionType'
            error={errors.constructionType?.message}
            register={register}
          >
            <option>Select</option>
            {constructionTypeOptions.map((construction: string) => (
              <option key={construction} value={construction}>
                {construction}
              </option>
            ))}
          </SelectField>
          <SelectField
            customClassName='col-span-6 lg:col-span-2'
            label='Acquisition Type'
            name='acquisitionType'
            error={errors.acquisitionType?.message}
            register={register}
          >
            <option>Select</option>
            {acquisitionTypeOptions.map((acquisition: string) => (
              <option key={acquisition} value={acquisition}>
                {acquisition}
              </option>
            ))}
          </SelectField>
          <SelectField
            customClassName='col-span-6 lg:col-span-2'
            label='Status of Site Control'
            name='siteControlStatus'
            error={errors.siteControlStatus?.message}
            register={register}
          >
            <option>Select</option>
            {siteControlStatusOptions.map((status: string) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </SelectField>
          <MaskedInputField
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='Estimated Acquisition Cost'
            name='acquisitionCostEstimate'
            id='acquisitionCostEstimate'
            error={errors.acquisitionCostEstimate?.message}
            control={control}
            MaskComponent={CurrencyFormat}
          />
          <MaskedInputField
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='Estimated Development Cost'
            name='developmentCostEstimate'
            id='developmentCostEstimate'
            error={errors.developmentCostEstimate?.message}
            control={control}
            MaskComponent={CurrencyFormat}
          />
          <MaskedInputField
            customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
            label='Estimated Pre-Development Cost'
            name='preDevelopmentCostEstimate'
            id='preDevelopmentCostEstimate'
            error={errors.preDevelopmentCostEstimate?.message}
            control={control}
            MaskComponent={CurrencyFormat}
          />
          <div className='col-span-6'>
            <label
              htmlFor='moreInfo'
              className='leading-7 block text-sm font-medium text-neutralColor-medium`'
            >
              More Information About Project
            </label>
            <textarea
              className='border rounded-md p-2 w-full focus:outline-none focus:ring-primaryColor-normal focus:border-primaryColor-normal'
              id='moreInfo'
              rows={5}
              placeholder='Please tell us more about this proposed project. Specifically highlight any timing sensitivity.'
              {...register('moreInfo')}
            ></textarea>
            {errors.moreInfo && (
              <p className='text-red-600 text-sm mt-1'>
                {errors.moreInfo?.message}
              </p>
            )}
          </div>
          <div className='col-span-6'>
            <FileInputField
              name='selectedFiles'
              label='Additional Docs'
              id='selectedFiles'
              placeholder='Upload Additional Documents'
              multiple
              {...{ control, watch, setValue, trigger }}
            />
          </div>
          {uploadError && (
            <p className='text-tertiaryColor-normal text-md col-span-6'>
              {uploadError}
            </p>
          )}
        </div>
        <div className='flex gap-4 justify-end mt-5'>
          <Button
            label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
            btnStyle={uploadFile?.isLoading ? 'btn-neutral' : 'btn-primary'}
            disabled={uploadFile.isLoading}
            type='submit'
          />
        </div>
      </div>
    </form>
  );
}
